import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getSitesByCategory, sites } from '@/mocks/sites';
import type { Site } from '@/mocks/sites';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = getCategoryBySlug(slug || '');
  const [activeSort, setActiveSort] = useState<'rating' | 'visitors' | 'newest'>('rating');
  const [filteredSites, setFilteredSites] = useState<Site[]>([]);
  const [activeTag, setActiveTag] = useState<string>('all');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  useEffect(() => {
    let result = getSitesByCategory(slug || '');

    if (activeSort === 'rating') {
      result = [...result].sort((a, b) => b.rating - a.rating);
    } else if (activeSort === 'visitors') {
      result = [...result].sort(
        (a, b) => parseFloat(b.visitors.replace('万', '')) - parseFloat(a.visitors.replace('万', ''))
      );
    }

    if (activeTag !== 'all') {
      result = result.filter((s) => s.tags.includes(activeTag));
    }

    setFilteredSites(result);
  }, [slug, activeSort, activeTag]);

  const allTags = ['all', ...Array.from(new Set(getSitesByCategory(slug || '').flatMap((s) => s.tags)))];

  if (!currentCategory) {
    return (
      <div className="min-h-screen bg-background-950">
        <Navbar />
        <main className="pt-20 px-4 md:px-6">
          <div className="max-w-7xl mx-auto text-center py-20">
            <i className="ri-emotion-sad-line text-6xl text-foreground-600 mb-4 block"></i>
            <h2 className="text-xl font-semibold text-foreground-200 mb-2">分类未找到</h2>
            <Link to="/" className="text-sm text-primary-400 hover:text-primary-300 cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 成人导航`,
    description: currentCategory.description,
    url: `https://adultnav.example.com/category/${currentCategory.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '成人导航',
      url: 'https://adultnav.example.com',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://adultnav.example.com' },
      { '@type': 'ListItem', position: 2, name: currentCategory.name, item: `https://adultnav.example.com/category/${currentCategory.slug}` },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${currentCategory.name} - 成人导航`}
        description={currentCategory.description}
        keywords={`成人导航,${currentCategory.name},成人${currentCategory.name}导航,成人网站导航,成人内容导航`}
        canonicalUrl={`/category/${currentCategory.slug}`}
        schemaData={{ ...categorySchema, ...breadcrumbSchema }}
      />

      <div className="min-h-screen bg-background-950">
        <Navbar />

        <main className="pt-20">
          <div className="px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-7xl mx-auto">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line text-[10px]"></i>
                <span className="text-foreground-300">{currentCategory.name}</span>
              </nav>

              <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4 mb-8">
                <div className="w-14 h-14 rounded-2xl bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                  <i className={`${currentCategory.icon} text-2xl text-primary-400`}></i>
                </div>
                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 mb-1">
                    {currentCategory.name}
                  </h1>
                  <p className="text-sm text-foreground-500">{currentCategory.description}</p>
                </div>
              </div>

              <div className="bg-background-900 border border-background-800/60 rounded-2xl p-2 mb-8">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {categories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/category/${cat.slug}`}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                          cat.slug === slug
                            ? 'bg-primary-500/20 text-primary-400'
                            : 'text-foreground-500 hover:text-foreground-300 hover:bg-background-800/60'
                        }`}
                      >
                        {cat.name}
                      </Link>
                    ))}
                  </div>

                  <div className="flex items-center gap-1.5 ml-auto">
                    <span className="text-xs text-foreground-600 mr-1 whitespace-nowrap">排序：</span>
                    {[
                      { key: 'rating', label: '评分' },
                      { key: 'visitors', label: '热度' },
                    ].map((s) => (
                      <button
                        key={s.key}
                        onClick={() => setActiveSort(s.key as typeof activeSort)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                          activeSort === s.key
                            ? 'bg-accent-500/20 text-accent-400'
                            : 'text-foreground-500 hover:text-foreground-300 hover:bg-background-800/60'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {allTags.length > 1 && (
                <div className="flex items-center gap-1.5 flex-wrap mb-6">
                  {allTags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => setActiveTag(tag)}
                      className={`px-3 py-1.5 rounded-lg text-xs transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        activeTag === tag
                          ? 'bg-secondary-500/20 text-secondary-400'
                          : 'bg-background-900 text-foreground-500 hover:text-foreground-300 border border-background-800/60'
                      }`}
                    >
                      {tag === 'all' ? '全部' : tag}
                    </button>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {filteredSites.map((site) => (
                  <div
                    key={site.id}
                    className="group bg-background-900 border border-background-800/60 hover:border-primary-500/30 rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                    data-product-shop
                  >
                    <div className="relative h-40 md:h-44 overflow-hidden">
                      <img
                        src={site.thumbnail}
                        alt={site.name}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 right-3">
                        <span className="px-2 py-1 bg-background-950/80 backdrop-blur-sm text-accent-400 text-xs rounded-md">
                          ★ {site.rating}
                        </span>
                      </div>
                      {site.featured && (
                        <div className="absolute top-3 left-3">
                          <span className="px-2 py-1 bg-primary-500 text-background-950 text-xs font-semibold rounded-md">
                            推荐
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-4 md:p-5">
                      <h3 className="text-sm md:text-base font-semibold text-foreground-100 mb-2">{site.name}</h3>
                      <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                        {site.description}
                      </p>
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {site.tags.map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 bg-secondary-500/10 text-secondary-400 text-[10px] rounded-md"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-foreground-600 flex items-center gap-1">
                          <i className="ri-user-line text-[10px]"></i> {site.visitors} 访问
                        </span>
                        <a
                          href={site.url}
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          className="px-4 py-1.5 bg-background-800 hover:bg-primary-500 text-foreground-300 hover:text-background-950 text-xs rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
                        >
                          访问网站
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {filteredSites.length === 0 && (
                <div className="text-center py-16">
                  <i className="ri-inbox-line text-5xl text-foreground-600 mb-4 block"></i>
                  <p className="text-sm text-foreground-500">暂无匹配的网站</p>
                </div>
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}